import React, { useRef, useEffect, useState } from 'react';

const ESTIMATED_TOOLTIP_WIDTH = 320;
const HALF_TOOLTIP_WIDTH = ESTIMATED_TOOLTIP_WIDTH / 2;

const VideoProgressBar = ({ currentFrame, totalFrames, onSeek, thumbnails, frameTimestamps }) => {
  const progressRef = useRef(null);
  const sliderHandleRef = useRef(null);

  const [previewData, setPreviewData] = useState({
    visible: false,
    x: 0,
    frame: 0,
    time: '00:00:00',
    imgSrc: null
  });

  useEffect(() => {
    const sliderElement = sliderHandleRef.current;
    if (sliderElement && totalFrames > 0) {
      const percent = (currentFrame / totalFrames) * 100;
      sliderElement.style.left = `${percent}%`;
    }
  }, [currentFrame, totalFrames]);

  const calculateFrameFromEvent = (e) => {
    const progressBar = progressRef.current;
    if (!progressBar) return 0;

    const rect = progressBar.getBoundingClientRect();
    let percent = (e.clientX - rect.left) / rect.width;

    // Clamp percentage between 0 and 1
    percent = Math.max(0, Math.min(1, percent));

    const frame = Math.round(percent * totalFrames);
    return Math.max(1, Math.min(frame, totalFrames));
  };

  const handleSeekClick = (e) => {
    const frame = calculateFrameFromEvent(e);
    onSeek(frame);
  };

  const handleMouseMove = (e) => {
    if (!totalFrames) return;

    const progressBar = progressRef.current;
    const rect = progressBar.getBoundingClientRect();
    const mouseXRelative = e.clientX - rect.left;

    // --- Edge Collision Calculation ---
    // Ensure the center of the tooltip (x) doesn't go off the edges
    const minX = HALF_TOOLTIP_WIDTH;
    const maxX = rect.width - HALF_TOOLTIP_WIDTH;

    let constrainedX = Math.max(minX, Math.min(mouseXRelative, maxX));

    // Safety check if bar is smaller than tooltip
    if (rect.width < ESTIMATED_TOOLTIP_WIDTH) {
      constrainedX = rect.width / 2;
    }
    // ----------------------------------

    const hoverFrame = calculateFrameFromEvent(e);

    const hoverTime = frameTimestamps && frameTimestamps[hoverFrame - 1]
      ? frameTimestamps[hoverFrame - 1]
      : '--:--:--';

    let imgSrc = null;
    if (thumbnails && thumbnails.length > 0) {
      const thumbIndex = Math.floor((hoverFrame - 1) / 100);
      if (thumbnails[thumbIndex]) {
        imgSrc = `${thumbnails[thumbIndex]}`;
      }
    }

    setPreviewData({
      visible: true,
      x: constrainedX,
      frame: hoverFrame,
      time: hoverTime,
      imgSrc: imgSrc
    });
  };

  const handleMouseLeave = () => {
    setPreviewData(prev => ({ ...prev, visible: false }));
  };

  return (
    <div
      className="vjs-progress-holder relative h-2 bg-gray-500 cursor-pointer mt-2 mb-4 group"
      onClick={handleSeekClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Progress Background */}
      <div ref={progressRef} className="w-full h-full"></div>

      {/* Slider Handle */}
      <div
        ref={sliderHandleRef}
        className="slider-handle absolute top-1/2 -mt-1.5 h-3 w-3 bg-red-600 rounded-full shadow pointer-events-none transform -translate-x-1/2"
      ></div>

      {/* Hover Preview Tooltip */}
      {previewData.visible && (
        <div
          className="absolute bottom-full mb-3 flex flex-col items-center bg-gray-900 text-white text-xs rounded shadow-lg p-1 z-50 pointer-events-none whitespace-nowrap"
          style={{
            left: `${previewData.x}px`,
            transform: 'translateX(-50%)'
          }}
        >
          {previewData.imgSrc ? (
            <img
              src={previewData.imgSrc}
              alt={`Frame ${previewData.frame}`}
              className="w-[300px] max-w-none h-auto mb-1 border border-gray-600 object-contain"
            />
          ) : (
            // Adjusted fallback width to match
            <div className="w-[300px] h-28 bg-black mb-1 flex items-center justify-center text-gray-500">
              No Preview
            </div>
          )}

          <div className="font-bold">Frame: {previewData.frame}</div>
          <div>Time: {previewData.time}</div>

          {/* Triangle arrow */}
          <div className="w-2 h-2 bg-gray-900 rotate-45 absolute -bottom-1 left-1/2 transform -translate-x-1/2"></div>
        </div>
      )}
    </div>
  );
};

export default VideoProgressBar;